/**
 * Stock spine inputs (PRD v2 §6.1–6.2). A leaf module like schemas/checklist.ts: it takes
 * the idempotency envelope from the spine and nothing imports it back.
 */
import { z } from 'zod';
import { idempotency } from './spine';
import { parseOpeningCostCents, parseOpeningQtyHundredths } from '../opening-stock';

/** Cosmetic labels only — the tree decides containment, not the kind (§6.1). */
export const STOCK_LOCATION_KINDS = ['warehouse', 'vehicle', 'bin'] as const;
export type StockLocationKind = (typeof STOCK_LOCATION_KINDS)[number];

export const STOCK_MOVEMENT_REASONS = [
  'count', // a physical count corrected the books
  'adjust', // damage, shrink, a mistake being fixed
  'transfer', // moved between locations (always written in pairs)
  'receipt', // arrived from a vendor
  'consume', // used on a job
  'return', // came back off a job
] as const;
export type StockMovementReason = (typeof STOCK_MOVEMENT_REASONS)[number];

/** Additive storage vocabulary. New adjustment input enforcement is Phase2 slice03. */
export const ADJUSTMENT_REASON_CODES = [
  'count_correction',
  'damaged',
  'stolen',
  'returned_to_supplier',
  'used_not_recorded',
  'other',
] as const;
export type AdjustmentReasonCode = (typeof ADJUSTMENT_REASON_CODES)[number];

export const ADJUSTMENT_REASON_LABELS: Record<AdjustmentReasonCode, string> = {
  count_correction: 'Count correction',
  damaged: 'Damaged',
  stolen: 'Stolen',
  returned_to_supplier: 'Returned to supplier',
  used_not_recorded: 'Used — not recorded',
  other: 'Other',
};

/**
 * Where a line on a job's parts list came from (ops insights Phase 6a): copied from the approved
 * quote the job was made from, or added by the office. Migration 0039's `job_parts_source_check`
 * holds a second copy of this list.
 */
export const JOB_PART_SOURCES = ['quote', 'office'] as const;
export type JobPartSource = (typeof JOB_PART_SOURCES)[number];

/** Quantities are entered as decimals and stored as numeric(12,2). */
const qty = z.coerce.number().finite().min(-1_000_000).max(1_000_000);

/**
 * The office's edits to a job's parts list (ops insights Phase 6a ticket 02). The quantity's
 * range (0.01 to 9,999.99) is the SERVER's to refuse, with a sentence (`planQtyProblem`), not a
 * schema bound: a refusal here would reach the card as a validation dump.
 */
export const jobPartAddInput = idempotency
  .extend({ jobId: z.string().uuid(), itemId: z.string().uuid(), qty })
  .strict();
export type JobPartAddInput = z.infer<typeof jobPartAddInput>;

/**
 * `expectedQtyHundredths`: the planned quantity the screen showed when it was changed. A tab
 * left open must not overwrite a newer quantity it never saw; the server refuses a mismatch.
 */
export const jobPartSetQtyInput = idempotency
  .extend({
    lineId: z.string().uuid(),
    qty,
    expectedQtyHundredths: z.number().int().min(0),
  })
  .strict();
export type JobPartSetQtyInput = z.infer<typeof jobPartSetQtyInput>;

export const jobPartRemoveInput = idempotency
  .extend({ lineId: z.string().uuid() })
  .strict();
export type JobPartRemoveInput = z.infer<typeof jobPartRemoveInput>;

export const jobPartsFromQuoteInput = idempotency
  .extend({ jobId: z.string().uuid() })
  .strict();
export type JobPartsFromQuoteInput = z.infer<typeof jobPartsFromQuoteInput>;

export const stockLocationCreateInput = idempotency.extend({
  name: z.string().trim().min(1).max(120),
  kind: z.enum(STOCK_LOCATION_KINDS).optional(),
  parentId: z.string().uuid().nullable().optional(),
  userId: z.string().uuid().nullable().optional(),
});
export type StockLocationCreateInput = z.infer<typeof stockLocationCreateInput>;

export const stockLocationUpdateInput = idempotency.extend({
  locationId: z.string().uuid(),
  name: z.string().trim().min(1).max(120).optional(),
  kind: z.enum(STOCK_LOCATION_KINDS).optional(),
  parentId: z.string().uuid().nullable().optional(),
  userId: z.string().uuid().nullable().optional(),
  active: z.boolean().optional(),
});
export type StockLocationUpdateInput = z.infer<typeof stockLocationUpdateInput>;

export const stockLocationIdInput = idempotency.extend({
  locationId: z.string().uuid(),
});
export type StockLocationIdInput = z.infer<typeof stockLocationIdInput>;

/** Turn a catalog row into something the shop counts — or stop counting it. */
export const itemTrackingInput = idempotency.extend({
  itemId: z.string().uuid(),
  tracked: z.boolean(),
  sku: z.string().trim().max(60).optional().or(z.literal('')),
});
export type ItemTrackingInput = z.infer<typeof itemTrackingInput>;

/** "There are 8 here." The server works out the correction. */
export const stockCountInput = idempotency.extend({
  itemId: z.string().uuid(),
  locationId: z.string().uuid(),
  countedQty: qty.min(0),
  note: z.string().trim().max(500).optional().or(z.literal('')),
});
export type StockCountInput = z.infer<typeof stockCountInput>;

/** Damage, shrink, a correction — a signed delta with a reason a human wrote. */
export const stockAdjustInput = idempotency
  .extend({
    itemId: z.string().uuid(),
    locationId: z.string().uuid(),
    qtyDelta: qty.refine((n) => n !== 0, 'An adjustment of zero changes nothing'),
    reasonCode: z.enum(ADJUSTMENT_REASON_CODES, {
      error: (issue) =>
        issue.input === undefined
          ? 'Choose an adjustment reason. If you already sent this change, check its stock history before entering it again.'
          : 'Choose one of the six adjustment reasons.',
    }),
    note: z.string().trim().max(500).optional().or(z.literal('')),
  })
  .strict()
  .superRefine((input, ctx) => {
    if (input.reasonCode === 'other' && !input.note)
      ctx.addIssue({ code: 'custom', path: ['note'], message: 'Add a note for Other.' });
    if (Boolean(input.clientId) !== Boolean(input.mutationId))
      ctx.addIssue({
        code: 'custom',
        path: ['mutationId'],
        message: 'Send both request keys, or neither.',
      });
  });
export type StockAdjustInput = z.infer<typeof stockAdjustInput>;

/**
 * A part went into the job (§6.2). Consumed at USE, never at quote — a part on a quote is a
 * proposal, and decrementing the van for a proposal is how a shop ends up ordering stock it
 * already has.
 *
 * `locationId` is optional because the answer is nearly always "my van": the server resolves
 * the caller's own vehicle when it's left out, so the common case is one tap.
 */
export const partsUsedInput = idempotency.extend({
  jobId: z.string().uuid(),
  workOrderLineId: z.string().uuid().optional(),
  visitId: z.string().uuid().optional(),
  itemId: z.string().uuid(),
  locationId: z.string().uuid().optional(),
  qty: qty.gt(0),
  note: z.string().trim().max(500).optional().or(z.literal('')),
});
export type PartsUsedInput = z.infer<typeof partsUsedInput>;

/**
 * It didn't get used after all — back on the van, and off the job's cost. `qty` puts back part
 * of it (used 5, put back 2, 3 stay on the job); absent means all that is left, which is what
 * an older client that never sends it has always meant.
 */
export const partsReturnInput = idempotency.extend({
  jobCostId: z.string().uuid(),
  qty: qty.gt(0).optional(),
  note: z.string().trim().max(500).optional().or(z.literal('')),
});
export type PartsReturnInput = z.infer<typeof partsReturnInput>;

/**
 * When to buy more of this part at this place (§6.5). A null point CLEARS the threshold —
 * "stop telling me about this one" has to be as easy as setting it.
 */
/** Reuse the decimal parser: binary multiplication rejects valid values such as1.1. */
export const keepFillQty = z
  .number()
  .finite()
  .refine(
    (n) => parseOpeningQtyHundredths(n) !== null,
    'Enter Keep/Fill from 0 to 1,000,000 with at most two decimals.',
  );
export const reorderPointInput = idempotency
  .extend({
    itemId: z.string().uuid(),
    locationId: z.string().uuid(),
    reorderPoint: z.coerce.number().pipe(keepFillQty).nullable(),
    reorderQty: qty.gt(0).nullable().optional(),
    fillTo: keepFillQty.nullable().optional(),
  })
  .superRefine((pair, ctx) => {
    if (
      pair.fillTo != null &&
      (pair.reorderPoint === null || pair.fillTo < pair.reorderPoint)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['fillTo'],
        message: 'Fill must be at least Keep.',
      });
    }
  });
export type ReorderPointInput = z.infer<typeof reorderPointInput>;

/** Who this part is normally bought from — null when the shop shops around. */
export const itemVendorInput = idempotency.extend({
  itemId: z.string().uuid(),
  vendorId: z.string().uuid().nullable(),
});
export type ItemVendorInput = z.infer<typeof itemVendorInput>;

/** One step, two ledger rows, one transaction (§6.2). */
export const stockTransferInput = idempotency.extend({
  workOrderLineId: z.string().uuid().optional(),
  itemId: z.string().uuid(),
  fromLocationId: z.string().uuid(),
  toLocationId: z.string().uuid(),
  qty: qty.gt(0),
  note: z.string().trim().max(500).optional().or(z.literal('')),
});
export type StockTransferInput = z.infer<typeof stockTransferInput>;

/** A deliberate one-time costed count; only inventory managers may initialize a basis. */
export const openingCountInput = idempotency
  .extend({
    itemId: z.string().uuid(),
    locationId: z.string().uuid(),
    countedQty: z.union([z.string(), z.number()]).transform((raw, ctx) => {
      const n = parseOpeningQtyHundredths(raw);
      if (n === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Enter an opening quantity from 0 to 1,000,000, with at most two decimals.',
        });
        return z.NEVER;
      }
      return n / 100;
    }),
    unitCostDollars: z.string().transform((raw, ctx) => {
      const n = parseOpeningCostCents(raw);
      if (n === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Enter an explicit unit cost from $0 to $1,000,000, with at most two decimals.',
        });
        return z.NEVER;
      }
      return n;
    }),
    note: z.string().trim().max(500).optional().or(z.literal('')),
  })
  .strict()
  .transform(({ unitCostDollars, ...rest }) => ({
    ...rest,
    unitCostCents: unitCostDollars,
  }));
export type OpeningCountInput = z.infer<typeof openingCountInput>;
